import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Tab } from '../hooks/multiWindow'

/**
 * Three-context split so consumers only re-render when the slice they
 * actually care about changes.
 *
 * - `TabsContext` — the full tabs array (changes on open/close/reorder).
 *   Consumers that render a list of all tabs (TabBar) subscribe here.
 *
 * - `ActiveTabContext` — the currently active tab + its id. Changes on every
 *   tab switch. Consumers that render the active tab's content or chrome
 *   (MainBreadcrumb, URL-sync) subscribe here.
 *
 * - `TabActionsContext` — stable handler references (updateTab,
 *   closeActiveTab). Consumers that only call actions (e.g. ProjectPage
 *   calling `updateTab` on title change) subscribe here and don't re-render
 *   when tabs/activeTabId change.
 *
 * `useTabContext()` (compatibility wrapper) still returns all five fields
 * for callers that legitimately need everything.
 */

// ── Tabs ────────────────────────────────────────────────────────────────
interface TabsContextValue {
  tabs: Tab[]
}
const TabsContext = createContext<TabsContextValue | null>(null)

// ── Active tab ──────────────────────────────────────────────────────────
interface ActiveTabContextValue {
  activeTabId: string | null
  activeTab: Tab | undefined
}
const ActiveTabContext = createContext<ActiveTabContextValue | null>(null)

// ── Actions ─────────────────────────────────────────────────────────────
interface TabActionsContextValue {
  updateTab: (tabId: string, updates: Partial<Tab>) => void
  closeActiveTab: () => void
  /** Open (or activate) a tab. Same shape as the underlying useTabManager. */
  openTab: (tab: Tab) => void
}
const TabActionsContext = createContext<TabActionsContextValue | null>(null)

// ── Provider ────────────────────────────────────────────────────────────
interface TabManagerProviderProps {
  tabs: Tab[]
  activeTabId: string | null
  activeTab: Tab | undefined
  updateTab: (tabId: string, updates: Partial<Tab>) => void
  closeActiveTab: () => void
  openTab: (tab: Tab) => void
  children: ReactNode
}

export function TabManagerProvider({
  tabs,
  activeTabId,
  activeTab,
  updateTab,
  closeActiveTab,
  openTab,
  children,
}: TabManagerProviderProps) {
  const tabsValue = useMemo(() => ({ tabs }), [tabs])
  const activeValue = useMemo(() => ({ activeTabId, activeTab }), [activeTabId, activeTab])
  const actionsValue = useMemo(
    () => ({ updateTab, closeActiveTab, openTab }),
    [updateTab, closeActiveTab, openTab],
  )

  return (
    <TabsContext.Provider value={tabsValue}>
      <ActiveTabContext.Provider value={activeValue}>
        <TabActionsContext.Provider value={actionsValue}>
          {children}
        </TabActionsContext.Provider>
      </ActiveTabContext.Provider>
    </TabsContext.Provider>
  )
}

// ── Hooks ───────────────────────────────────────────────────────────────
export function useTabs() {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('useTabs must be used within <TabManagerProvider>')
  return ctx
}

export function useActiveTab() {
  const ctx = useContext(ActiveTabContext)
  if (!ctx) throw new Error('useActiveTab must be used within <TabManagerProvider>')
  return ctx
}

export function useTabActions() {
  const ctx = useContext(TabActionsContext)
  if (!ctx) throw new Error('useTabActions must be used within <TabManagerProvider>')
  return ctx
}

/**
 * Compatibility wrapper that returns all five fields at once. New callers
 * should prefer the narrower hooks (`useTabs`, `useActiveTab`,
 * `useTabActions`) to avoid unnecessary re-renders.
 *
 * @example
 * const { activeTab, updateTab } = useTabContext()
 * updateTab(activeTab.id, { title: 'New Title' })
 */
export function useTabContext() {
  const { tabs } = useTabs()
  const { activeTabId, activeTab } = useActiveTab()
  const { updateTab, closeActiveTab } = useTabActions()
  return { tabs, activeTabId, activeTab, updateTab, closeActiveTab }
}
